import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { APIAdapterFactory } from './APIAdapterFactory';
import { isE2ETestMode } from '../../utils/apiMode';

/** Interface for accessing adapter baseURL */
interface AdapterWithBaseURL {
  baseURL: string;
}

/**
 * Generic HTTP client for making API calls
 * Automatically selects between local and serverless APIs based on current adapter
 * This provides a simple get/post/put/delete interface for services that need
 * to make generic HTTP calls beyond the specialized adapter methods.
 */
class HTTPClient {
  private async getClient(): Promise<AxiosInstance> {
    const adapter = await APIAdapterFactory.getAdapter();
    const baseURL = (adapter as unknown as AdapterWithBaseURL).baseURL;

    // Create a new axios instance with the correct baseURL.
    // E2E bypass header injected per-request via isE2ETestMode() — never
    // leaks to real users because the flag lives in localStorage.
    return axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(isE2ETestMode() ? { 'X-E2E-Test': 'true' } : {}),
      },
    });
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const client = await this.getClient();
    return client.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const client = await this.getClient();
    return client.post<T>(url, data, config);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const client = await this.getClient();
    return client.put<T>(url, data, config);
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const client = await this.getClient();
    return client.delete<T>(url, config);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const client = await this.getClient();
    return client.patch<T>(url, data, config);
  }
}

export const api = new HTTPClient();
